import { HttpStatusCode } from "axios";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import WorkIcon from "@mui/icons-material/Work";
import Box from "@mui/material/Box";
import { alpha, styled, type CSSObject, type Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import PageTitleBreadcrumb, { breadcrumbHeadingName } from "_shared/_views/breadcrumbs/page-title-breadcrumb";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import useSessionUser from "permissions/session/use-session-user";
import WhoHasAccessButton from "permissions/_views/records/who-has-access-button";
import { useWhoHasAccess } from "permissions/_views/records/who-has-access";
import useConfirmLeavingUnsavedEdits from "work/_views/use-confirm-leaving-unsaved-edits";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { startDraftProposalFromTeamTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  useArchiveTeamTemplateMutation,
  useDeleteTeamTemplateMutation,
  useGetTeamTemplateByIdQuery,
  useUnarchiveTeamTemplateMutation,
} from "work/team-templates/_api/team-template-api";
import {
  getErrorLoadingTeamTemplateById,
  getIsLoadingTeamTemplateById,
  getTeamTemplateById,
} from "work/team-templates/_store/team-templates-redux-slice";
import TeamTemplateForm from "work/team-templates/_views/team-template-form";
import { PANE_STRETCH_ATTRIBUTE } from "_shared/_views/tables/table-pane-stretch";
import { narrowPaneStretchedTable } from "work/_views/work-pane-layout";
import { ARCHIVED_TEMPLATES_PATH, TEMPLATES_PATH } from "_shared/teams/teams-list-chips";

const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "dimmed",
})<{
  dimmed?: boolean;
}>(({ dimmed, theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
  "&::after": {
    backgroundColor: alpha(theme.palette.background.default, 0.38),
    content: '""',
    inset: 0,
    opacity: dimmed ? 1 : 0,
    pointerEvents: "none",
    position: "absolute",
    transition: theme.transitions.create("opacity", {
      duration: dimmed ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      easing: dimmed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    }),
    zIndex: theme.zIndex.drawer - 1,
  },
}));

/**
 * The inset on either side of a phone's page — one number for the header and the form, so the title,
 * the Team Name field and the roster's search stand the same distance off both edges.
 *
 * A viewport query rather than a pane's container query: this page has no docked drawer beside it,
 * and below Material's `lg` the navigation drawer is temporary, so on a phone the window is the page.
 */
const PHONE_INSET_UNITS = 2;

function onAPhone(theme: Theme): string {
  return theme.breakpoints.down("sm");
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  // The title keeps its line and the actions sit beside it while they fit; an edit page's four do
  // not on a phone, nor its five with Who has access (#6839) on a laptop, and wrap to a line of their
  // own beneath the title instead of running past the edge.
  columnGap: theme.spacing(2),
  flexWrap: "wrap",
  rowGap: theme.spacing(1),
  [onAPhone(theme)]: {
    columnGap: theme.spacing(1),
    padding: theme.spacing(2, PHONE_INSET_UNITS, 1),
  },
}));

const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "row",
  minWidth: 0,
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [onAPhone(theme)]: {
    fontSize: "1.25rem",
  },
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  // A button that runs out of room moves to the next line whole, rather than its label taking two.
  "& .MuiButton-root": { whiteSpace: "nowrap" },
  [onAPhone(theme)]: {
    gap: theme.spacing(1),
    // A large button's 22px of padding a side is what put a create page's Delete, Archive and Save on
    // two lines; at 12px the three share one under the title.
    "& .MuiButton-root": { paddingInline: theme.spacing(1.5) },
    "& .MuiButton-startIcon": { marginRight: theme.spacing(0.5) },
  },
}));

const SaveButtonContainer = styled("span")(() => ({
  display: "inline-flex",
}));

const CONTENT_INSET_UNITS = 3;

const Content = styled("section")(({ theme }) => ({
  flex: 1,
  overflow: "auto",
  padding: theme.spacing(2, CONTENT_INSET_UNITS, 3),
  ...stretchedRosterRows(theme, CONTENT_INSET_UNITS),
  [onAPhone(theme)]: {
    padding: theme.spacing(1, PHONE_INSET_UNITS, 0),
    ...phoneStretchedRoster(theme),
  },
}));

/**
 * The roster's rows run from the navigation drawer to the window's right edge at every width, not just
 * on a phone. The table hangs out of the content's inset into its padding on both sides, and its
 * toolbar takes the inset back as margin, so the search and the leader control stay under the Team Name
 * field and only the header, the rows and their scrollbar reach the edges.
 *
 * Scoped to this page's `Content` and to the roster's own mark, `PANE_STRETCH_ATTRIBUTE`, so the vendor
 * team view and the roster table keep their layout everywhere else they are used: a draft proposal's
 * Team tab, restaffing, a work pane. The phone rule below replaces this one with the work pane's.
 */
function stretchedRosterRows(theme: Theme, insetUnits: number): CSSObject {
  const inset = theme.spacing(insetUnits);
  return {
    [`& [${PANE_STRETCH_ATTRIBUTE}]`]: {
      marginLeft: `-${inset}`,
      marginRight: `-${inset}`,
      maxWidth: "none",
      width: `calc(100% + 2 * ${inset})`,
      // The view buttons' last glyph, not its button, is what ends on the Save button's right edge: an
      // icon button pads its glyph by 8px, so the toolbar runs that much further right than the inset.
      "& > :not(.MuiTableContainer-root)": {
        marginLeft: inset,
        marginRight: `calc(${inset} - ${theme.spacing(1)})`,
      },
      // MRT pads its toolbar's row by 8px, which stood the search 8px inside the Team Name field's
      // left edge. The inset alone is the toolbar's edge, as it is on a phone.
      "& > :not(.MuiTableContainer-root) > .MuiBox-root": { paddingLeft: 0, paddingRight: 0 },
      // MRT keeps an empty box for its own toolbar buttons at the row's end, and the row's 8px gap
      // before it held the view buttons that much further off the right edge.
      "& > :not(.MuiTableContainer-root) > .MuiBox-root > .MuiBox-root:last-child:has(> :only-child:empty)": {
        display: "none",
      },
    },
  };
}

/**
 * On a phone the roster runs edge to edge and down to the bottom of the page, as a work pane's tables
 * do at the same width (see `narrowPaneStretchedTable` in `work/_views/work-pane-layout`).
 *
 * The roster table marks its own paper with `PANE_STRETCH_ATTRIBUTE`, so the work pane's rule reaches
 * it through the vendor team view without that view knowing. Sharing that rule also gives the
 * roster's toolbar the same edges as a work pane's: the heading, search and add control start on the
 * Team Name field's left edge and the last toolbar icon ends on its right one (#5907).
 */
function phoneStretchedRoster(theme: Theme): CSSObject {
  return {
    display: "flex",
    flexDirection: "column",
    ...narrowPaneStretchedTable(theme, PHONE_INSET_UNITS),
  };
}

const LoaderContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  height: "100%",
  justifyContent: "center",
}));

const SELECTION_DRAWER_WIDTH = "min(500px, 100vw)";
const DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS = 250;

type TeamTemplateAction = "create" | "edit" | "view";
const TeamTemplateActions = new Set<TeamTemplateAction>(["create", "edit", "view"]);

export default function TeamTemplatePage() {
  const { action: actionParam, id: idParam } = useParams();
  const pageAction: TeamTemplateAction = (actionParam as TeamTemplateAction) ?? "create";
  const templateId = React.useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const location = useLocation();

  if (!TeamTemplateActions.has(pageAction)) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }

  if (pageAction !== "create" && !templateId) {
    throw new Response("Not Found", { status: HttpStatusCode.NotFound });
  }

  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  // Every move this page makes is one it has already asked about or one a save, delete or archive
  // finished, so it navigates through the guard's pass rather than prompting again.
  const navigate = useConfirmLeavingUnsavedEdits(isFormDirty);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [isSelectionDrawerOpen, setIsSelectionDrawerOpen] = React.useState(false);
  const [saveButtonContainer, setSaveButtonContainer] = React.useState<HTMLSpanElement | null>(null);
  const [deleteTeamTemplate] = useDeleteTeamTemplateMutation();
  const [archiveTeamTemplate] = useArchiveTeamTemplateMutation();
  const [unarchiveTeamTemplate] = useUnarchiveTeamTemplateMutation();
  const teamTemplateAccess = useWhoHasAccess("team-templates", "the team template");
  const teamTemplateQuery = useGetTeamTemplateByIdQuery(
    pageAction !== "create" && templateId ? { templateId: templateId.value } : skipToken,
  );

  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionUser = useSessionUser();
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const teamTemplate = useSelector(getTeamTemplateById(templateId));
  const isLoadingTeamTemplate =
    useSelector(getIsLoadingTeamTemplateById(templateId)) || teamTemplateQuery.isLoading || teamTemplateQuery.isFetching;
  const errorLoadingTeamTemplate = useSelector(getErrorLoadingTeamTemplateById(templateId)) || teamTemplateQuery.isError;
  const isTeamTemplateMissing = teamTemplateQuery.data === null || errorLoadingTeamTemplate;
  const disableEditing = pageAction === "view";
  const isArchivedTemplateRoute = location.pathname.startsWith("/teams/archived/");
  const isTemplateArchived = isArchivedTemplateRoute || Boolean(teamTemplate?.archived);
  // The list it came from: the Templates tab, or its Archived chip (#6718).
  const listPath = isArchivedTemplateRoute ? ARCHIVED_TEMPLATES_PATH : TEMPLATES_PATH;

  React.useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  React.useEffect(() => {
    if (pageAction === "create" || !templateId || teamTemplate || isLoadingTeamTemplate || isDeleting) return;

    if (!isTeamTemplateMissing) return;
    enqueueSnackbar("Team template not found", { variant: "warning" });
    navigate(listPath, { replace: true });
  }, [
    isDeleting,
    isLoadingTeamTemplate,
    isTeamTemplateMissing,
    listPath,
    navigate,
    pageAction,
    teamTemplate,
    templateId,
  ]);

  React.useEffect(() => {
    globalThis.dispatchEvent(new Event("resize"));

    const timeoutId = globalThis.setTimeout(() => {
      globalThis.dispatchEvent(new Event("resize"));
    }, DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [isSelectionDrawerOpen]);

  const handleNavigateBack = React.useCallback(async () => {
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
    }

    navigate(listPath, { replace: true });
  }, [confirm, isFormDirty, listPath, navigate]);

  async function handleDeleteClicked() {
    if (!templateId) return;

    try {
      const response = await confirm({
        title: "Delete Team Template?",
        message: "This cannot be undone.",
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      await deleteTeamTemplate({ templateId: templateId.value }).unwrap();
      enqueueSnackbar("Deleted vendor team template", { variant: "success" });
      navigate(TEMPLATES_PATH, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete vendor team template. Please try again", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleArchiveClicked() {
    if (!templateId || !teamTemplate) return;

    const shouldArchive = !isTemplateArchived;
    const action = shouldArchive ? "Archive" : "Unarchive";

    try {
      const response = await confirm({
        title: `${action} Team Template?`,
        message: `${action} team template?`,
        okButtonText: action,
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      setIsArchiving(true);
      if (shouldArchive) {
        await archiveTeamTemplate({ templateId: templateId.value }).unwrap();
      } else {
        await unarchiveTeamTemplate({ templateId: templateId.value }).unwrap();
      }
      enqueueSnackbar(`${action}d vendor team template`, { variant: "success" });
      navigate(shouldArchive ? ARCHIVED_TEMPLATES_PATH : TEMPLATES_PATH, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action.toLowerCase()} vendor team template. Please try again`, { variant: "error" });
    } finally {
      setIsArchiving(false);
    }
  }

  // The draft starts from the saved template, and it goes through this page's `navigate`, which the
  // guard does not question. So the edits on screen would be dropped without a word: ask here first
  // (review of #7070).
  const handleCreateProposalClicked = React.useCallback(async () => {
    if (!teamTemplate) return;
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "The proposal starts from the saved template. Creating it discards the changes you have not saved.",
        okButtonText: "Discard",
        cancelButtonText: "Cancel",
      });
      if (response !== ConfirmResponse.Ok) return;
    }
    startDraftProposalFromTeamTemplate({
      dispatch,
      navigate,
      session,
      template: teamTemplate,
    });
  }, [confirm, dispatch, isFormDirty, navigate, session, teamTemplate]);

  if (!sessionPermissions) {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;
  // Authoring a team template needs read/write team access — see `User.canCreateTeamTemplates`.
  // Refusing here rather than at Save is the point: every route into a new template (the Teams page's
  // Create Template button, a pasted URL) lands on this component, so this one gate stops anyone
  // filling in a whole template the API was always going to answer 403 to (issue #4948).
  if (pageAction === "create" && !sessionUser?.canCreateTeamTemplates()) {
    return (
      <NotAuthorized message="You do not have permission to create a team template. Ask an administrator at your firm for read/write team access." />
    );
  }

  let pageTitle = "Edit Template";
  if (pageAction === "create") {
    pageTitle = "Create";
  } else if (pageAction === "view") {
    pageTitle = "View";
  }

  return (
    <PageContainer>
      <Main dimmed={isSelectionDrawerOpen}>
        <HeaderContainer>
          <TitleContainer>
            <PageTitle variant="h4" aria-label={breadcrumbHeadingName(["Teams"], pageTitle)}>
              {/*
                * The way back is the breadcrumb, not a "Back" chip beside it (#5907). It still asks
                * before discarding an edit, which is why it hands the click to `handleNavigateBack`
                * rather than following the link itself.
                */}
              <PageTitleBreadcrumb label="Teams" to={listPath} replace onFollow={handleNavigateBack} />
              {pageTitle}
            </PageTitle>
          </TitleContainer>
          <ActionButtonsContainer>
            {teamTemplateAccess.drawer}
            {/* Reading who holds a grant changes nothing, so it comes before the actions that do. */}
            {teamTemplateAccess.mayOpen && pageAction !== "create" && teamTemplate?.id && (
              <WhoHasAccessButton
                iconOnly
                size="large"
                onClick={() => {
                  if (teamTemplate?.id) teamTemplateAccess.open(teamTemplate.id);
                }}
              />
            )}
            {pageAction === "edit" && !isTemplateArchived && (
              <LoadingButton
                variant="outlined"
                size="large"
                color="primary"
                loading={false}
                disabled={isLoadingTeamTemplate || !teamTemplate}
                startIcon={<WorkIcon />}
                onClick={handleCreateProposalClicked}
              >
                Create Proposal
              </LoadingButton>
            )}
            {pageAction === "edit" && (
              <LoadingButton
                variant="outlined"
                size="large"
                color="error"
                loading={isDeleting}
                disabled={isDeleting || isArchiving || isLoadingTeamTemplate}
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClicked}
              >
                Delete
              </LoadingButton>
            )}
            {pageAction === "edit" || isTemplateArchived ? (
              <LoadingButton
                variant="outlined"
                size="large"
                color="inherit"
                loading={isArchiving}
                disabled={isDeleting || isArchiving || isLoadingTeamTemplate || !teamTemplate}
                startIcon={isTemplateArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                onClick={handleArchiveClicked}
              >
                {isTemplateArchived ? "Unarchive" : "Archive"}
              </LoadingButton>
            ) : null}
            <SaveButtonContainer ref={setSaveButtonContainer} />
          </ActionButtonsContainer>
        </HeaderContainer>
        <Content>
          {pageAction === "edit" && isLoadingTeamTemplate && !teamTemplate ? (
            <LoaderContainer>
              <Loader fontSize="20px" />
            </LoaderContainer>
          ) : (
            <TeamTemplateForm
              templateId={templateId}
              disableEditing={disableEditing}
              navigate={navigate}
              onFormDirty={(dirty) => setIsFormDirty(dirty === true)}
              saveButtonContainer={saveButtonContainer}
              saveButtonLabel="Save"
              onSelectionDrawerOpenChange={setIsSelectionDrawerOpen}
              onSave={(template) => {
                setIsFormDirty(false);
                if (pageAction === "create" && template.id) {
                  navigate(`/teams/templates/edit/${template.id.value}`, { replace: true });
                }
              }}
            />
          )}
        </Content>
      </Main>
      <div style={{ width: isSelectionDrawerOpen ? SELECTION_DRAWER_WIDTH : 0 }} />
    </PageContainer>
  );
}
